/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Select, Text, TextInput } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { moneyInputMask, moneyMask } from '@/shared/utils/input-masks';
import {
  KNOWN_GUARANTEE_KINDS,
  type RentalTerms,
  type SaleTerms,
} from '@/services/requests/housien-services/DealsService';

const labelClasses = cn(
  'text-[length:var(--text-meta)]',
  'font-[weight:var(--font-medium)]',
  'text-(--text-secondary)',
);

/** The value that stands for "one we do not have a name for yet". */
const OTHER = '__other__';

/**
 * A labelled field, so each block below reads as a list of questions.
 */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
      <Text as="label" className={labelClasses}>{label}</Text>
      {children}
    </fieldset>
  );
}

/**
 * The first entry of a multi-select value, or the raw string as-is.
 */
function singleValue(raw: string | string[] | null): string {
  return Array.isArray(raw) ? (raw[0] ?? '') : (raw ?? '');
}

/**
 * The guarantee, which is what a landlord actually decides on.
 *
 * The five known kinds are offered as suggestions and the list stays
 * open: an agency arranges guarantees the way it arranges everything
 * else, and refusing an unlisted one would push the broker into the
 * notes, where nothing groups. The backend folds whatever arrives, so
 * "Seguro Fiança" and "seguro fianca" land on one entry rather than two.
 */
function GuaranteeFields({ terms, onChange }: {
  terms: RentalTerms;
  onChange: (terms: RentalTerms) => void;
}) {
  const { t } = useTranslation('features/deals');
  const guarantee = terms.guarantee ?? { kind: '' };
  const isKnown = (KNOWN_GUARANTEE_KINDS as readonly string[]).includes(guarantee.kind);
  const [freeForm, setFreeForm] = useState(Boolean(guarantee.kind) && !isKnown);

  const setKind = (kind: string) => onChange({ ...terms, guarantee: { ...guarantee, kind } });

  return (
    <>
      <Field label={t('terms.guarantee')}>
        <Select
          name="guarantee-kind"
          data-testid="guarantee-kind"
          value={freeForm ? OTHER : guarantee.kind}
          onChange={(raw) => {
            const chosen = singleValue(raw);
            setFreeForm(chosen === OTHER);
            setKind(chosen === OTHER ? '' : chosen);
          }}
          data={[
            ...KNOWN_GUARANTEE_KINDS.map((kind) => ({
              value: kind, label: t(`terms.guarantees.${kind}`),
            })),
            { value: OTHER, label: t('terms.guaranteeOther') },
          ]}
          placeholder={t('terms.unspecified')}
          clearable
        />
      </Field>

      {freeForm && (
        <Field label={t('terms.guaranteeOtherLabel')}>
          <TextInput
            data-testid="guarantee-kind-other"
            value={guarantee.kind}
            onChange={(e) => setKind(e.target.value)}
            placeholder={t('terms.guaranteeOtherPlaceholder')}
          />
        </Field>
      )}

      <Field label={t('terms.guaranteeAmount')}>
        <TextInput
          data-testid="guarantee-amount"
          inputMode="decimal"
          value={guarantee.amount != null ? moneyMask(guarantee.amount.toFixed(2)).masked : ''}
          onChange={(e) => {
            const unmasked = moneyInputMask(e.target.value).unmasked;
            onChange({
              ...terms,
              guarantee: { ...guarantee, amount: unmasked ? Number(unmasked) : null },
            });
          }}
          placeholder={t('terms.guaranteeAmountPlaceholder')}
        />
      </Field>
    </>
  );
}

/**
 * The rental block: the guarantee first, because it decides the answer.
 */
function RentalFields({ terms, onChange }: {
  terms: RentalTerms;
  onChange: (terms: RentalTerms) => void;
}) {
  const { t } = useTranslation('features/deals');

  return (
    <>
      <GuaranteeFields terms={terms} onChange={onChange} />

      <Field label={t('terms.termMonths')}>
        <TextInput
          data-testid="term-months"
          inputMode="numeric"
          value={terms.term_months != null ? String(terms.term_months) : ''}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '');
            onChange({ ...terms, term_months: digits ? Number(digits) : null });
          }}
          placeholder={t('terms.termMonthsPlaceholder')}
        />
      </Field>

      <Field label={t('terms.iptuPaidBy')}>
        <Select
          name="iptu-paid-by"
          data-testid="iptu-paid-by"
          value={terms.iptu_paid_by ?? ''}
          onChange={(raw) => onChange({
            ...terms,
            iptu_paid_by: (singleValue(raw) || null) as RentalTerms['iptu_paid_by'],
          })}
          data={[
            { value: 'tenant', label: t('terms.payers.tenant') },
            { value: 'landlord', label: t('terms.payers.landlord') },
          ]}
          placeholder={t('terms.unspecified')}
          clearable
        />
      </Field>
    </>
  );
}

/**
 * The conditions a sale offer still waits on.
 *
 * Not paperwork: accepting an offer that still has a pending condition
 * deliberately does not close the attendance, so an unrecorded
 * condition is the difference between a win and a negotiation.
 *
 * The `condition_id` is left to the backend, which generates one.
 */
function ConditionFields({ terms, onChange }: {
  terms: SaleTerms;
  onChange: (terms: SaleTerms) => void;
}) {
  const { t } = useTranslation('features/deals');
  const conditions = terms.conditions ?? [];

  const write = (next: typeof conditions) => onChange({ ...terms, conditions: next });

  return (
    <Field label={t('terms.conditions')}>
      {conditions.map((condition, index) => (
        <div key={index} className="flex items-center gap-(--space-2)">
          <TextInput
            data-testid="condition-description"
            value={condition.description}
            onChange={(e) => write(conditions.map((c, i) => (
              i === index ? { ...c, description: e.target.value } : c
            )))}
            placeholder={t('terms.conditionPlaceholder')}
          />
          <Button
            variant="ghost-text"
            size="micro"
            onClick={() => write(conditions.filter((_, i) => i !== index))}
            data-testid="remove-condition-button"
          >
            {t('terms.removeCondition')}
          </Button>
        </div>
      ))}
      <Button
        variant="ghost-text"
        size="micro"
        onClick={() => write([
          ...conditions,
          { condition_id: '', description: '', state: 'pending' as const },
        ])}
        data-testid="add-condition-button"
      >
        {t('terms.addCondition')}
      </Button>
      <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary)">
        {t('terms.conditionsHint')}
      </Text>
    </Field>
  );
}

/**
 * The sale block: how it gets paid for, and what it waits on.
 */
function SaleFields({ terms, onChange }: {
  terms: SaleTerms;
  onChange: (terms: SaleTerms) => void;
}) {
  const { t } = useTranslation('features/deals');

  return (
    <>
      <Field label={t('terms.paymentMethod')}>
        <Select
          name="payment-method"
          data-testid="payment-method"
          value={terms.payment_method ?? ''}
          onChange={(raw) => onChange({
            ...terms,
            payment_method: (singleValue(raw) || null) as SaleTerms['payment_method'],
          })}
          data={['cash', 'financed', 'fgts', 'consortium', 'permuta'].map((method) => ({
            value: method, label: t(`terms.payments.${method}`),
          }))}
          placeholder={t('terms.unspecified')}
          clearable
        />
      </Field>

      {terms.payment_method === 'financed' && (
        <Field label={t('terms.bank')}>
          <TextInput
            data-testid="financing-bank"
            value={terms.bank ?? ''}
            onChange={(e) => onChange({ ...terms, bank: e.target.value })}
            placeholder={t('terms.bankPlaceholder')}
          />
        </Field>
      )}

      <ConditionFields terms={terms} onChange={onChange} />
    </>
  );
}

interface TermsFieldsProps {
  dealType: 'sale' | 'rental';
  rentalTerms: RentalTerms;
  saleTerms: SaleTerms;
  onRentalChange: (terms: RentalTerms) => void;
  onSaleChange: (terms: SaleTerms) => void;
}

/**
 * The terms of one offer, whichever kind of offer it is.
 *
 * `deal_type` chooses the block and only one is ever rendered: the
 * backend refuses rental terms on a sale outright, so a form offering
 * both would be an invitation to a 400.
 *
 * Everything here is optional. A broker who has only the number must
 * still be able to record it -- demanding the whole picture would send
 * them back to recording nothing, which is the habit this replaces.
 *
 * @param props - The offer's type, both term states, and their setters.
 * @returns The fields belonging to the offer's own kind.
 */
export function ProposalTermsFields({
  dealType, rentalTerms, saleTerms, onRentalChange, onSaleChange,
}: TermsFieldsProps) {
  return dealType === 'rental'
    ? <RentalFields terms={rentalTerms} onChange={onRentalChange} />
    : <SaleFields terms={saleTerms} onChange={onSaleChange} />;
}

/**
 * Strips the empty shell a form leaves behind.
 *
 * A form always has term objects, even when the broker typed nothing in
 * them; posting `{}` would record an offer that claims to have terms and
 * says nothing, so an untouched block is sent as absent instead.
 *
 * @param terms - The terms as the form holds them.
 * @returns The terms, or undefined when nothing was filled in.
 */
export function omitEmptyTerms<T extends RentalTerms | SaleTerms>(terms: T): T | undefined {
  const filled = Object.entries(terms).filter(([, value]) => {
    if (value == null || value === '') return false;
    return Array.isArray(value) ? value.length > 0 : true;
  });
  return filled.length > 0 ? (Object.fromEntries(filled) as T) : undefined;
}

export default ProposalTermsFields;
